import { FC, useEffect, useRef, useState } from 'react';

import cs from 'classnames';
import { motion } from 'framer-motion';

import useResizeObserver from 'hooks/useResizeObserver';

import {
  ANIMATION_DEFAULT_DIRECTION,
  ANIMATION_DEFAULT_PAUSE_DURATION,
  ANIMATION_DEFAULT_SPEED
} from 'constants/commons';

import styles from './AnimatedImageHorizontal.module.scss';

interface AnimatedImageHorizontalProps {
  src: string;
  speed?: number;
  initialDirection?: 'left' | 'right';
  className?: string;
  pauseDuration?: number;
}

const AnimatedImageHorizontal: FC<AnimatedImageHorizontalProps> = ({
  src,
  speed = ANIMATION_DEFAULT_SPEED,
  initialDirection = ANIMATION_DEFAULT_DIRECTION,
  className,
  pauseDuration = ANIMATION_DEFAULT_PAUSE_DURATION
}) => {
  const { dimensions, resizeObserverRef } = useResizeObserver();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  const [containerWidth, setContainerWidth] = useState(0);
  const [imageWidth, setImageWidth] = useState(0);

  useEffect(() => {
    if (containerRef.current) {
      const { width } = containerRef.current.getBoundingClientRect();
      setContainerWidth(width);
    }
  }, [dimensions.width]);

  useEffect(() => {
    if (!imageRef.current) return;
    const imageNode = imageRef.current;

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setImageWidth(entry.contentRect.width);
      }
    });

    observer.observe(imageNode);
    return () => observer.disconnect();
  }, []);

  const minOffset = containerWidth - imageWidth;
  const startX = initialDirection === 'right' ? minOffset : 0;
  const endX = initialDirection === 'right' ? 0 : minOffset;

  return (
    <div
      ref={(node) => {
        containerRef.current = node;
        resizeObserverRef.current = node;
      }}
      className={cs(styles.wrapperContainer, className)}
    >
      <motion.img
        ref={imageRef}
        src={src}
        className={styles.image}
        initial={{ x: startX }}
        animate={{ x: [startX, endX] }}
        transition={{
          duration: speed,
          ease: 'easeInOut',
          repeat: Infinity,
          repeatType: 'reverse',
          repeatDelay: pauseDuration / 1000
        }}
      />
    </div>
  );
};

export default AnimatedImageHorizontal;
